import React from 'react';
import {
    CompetitiveRoundSummariesDeathEventInner,
    Maps
} from "../../reckon-api";
import Box from "@mui/material/Box";
import {Circle, ImageOverlay, MapContainer, Polyline, Rectangle, useMap} from "react-leaflet";
import {CRS, LatLngBounds} from "leaflet";
import {useEffect, useMemo, useRef, useState} from "react";
import 'leaflet/dist/leaflet.css';
import "./Map.css";
import {CircularProgress, Typography} from "@mui/material";
import {formatDuration} from "../../utils";
import SimpleHeatLayer, { SimpleHeatPoint } from "./SimpleHeatLayer";
import AgentIcon from "../Icons/AgentIcon";
import WeaponIcon from "../Icons/WeaponIcon";

export function positionToMapBounds(position: {x:number, y:number}, map: Maps){
    return {
        y: (position.y - map.min_y) / (map.max_y - map.min_y),
        x: (position.x - map.min_x) / (map.max_x - map.min_x),
    }
}

const PLAYER_POINT_COLORS = [
    '#2ecc71',
    '#3498db',
    '#e74c3c',
    '#f1c40f',
    '#9b59b6',
    '#1abc9c',
    '#e67e22',
    '#ff6b9d',
    '#00cec9',
    '#a29bfe',
    '#fd79a8',
    '#55efc4',
];

export function getPlayerPointColor(playerId: string | null | undefined) {
    const key = (playerId || 'unknown').trim() || 'unknown';
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = ((hash << 5) - hash) + key.charCodeAt(i);
        hash |= 0;
    }
    return PLAYER_POINT_COLORS[Math.abs(hash) % PLAYER_POINT_COLORS.length];
}

// Kill events already include killer/victim locations from the API
export type KillEventWithLocation = CompetitiveRoundSummariesDeathEventInner;

export type KillMapRenderMode = 'points' | 'heatmap';
export type KillPositionSource = 'victim' | 'killer';

export type SelectableKillEvent = KillEventWithLocation & {
    selectionIndex?: number;
    enabled?: boolean;
    player?: string;
};

type MapPoint = {
    selectionIndex: number;
    enabled: boolean;
    lat: number;
    lng: number;
    label: string;
    playerLabel: string;
    timeLabel: string;
    color: string;
    killerAgent: string;
    victimAgent: string;
    killerWeapon: string;
    victimWeapon: string;
    isHeadshot: boolean;
    isFirstBlood: boolean;
    positionSource: KillPositionSource;
    killerLat: number | null;
    killerLng: number | null;
    victimLat: number | null;
    victimLng: number | null;
};

const DIMMED_POINT_COLOR = 'rgba(160,160,170,0.45)';

function KillPointTooltip(props: { point: MapPoint }) {
    const { point } = props;
    const accent = point.color;

    return (
        <Box
            sx={{
                minWidth: 220,
                maxWidth: 260,
                background: 'rgba(8, 10, 16, 0.97)',
                borderRadius: '12px',
                overflow: 'hidden',
                border: `1px solid ${accent}45`,
                boxShadow: `
                    0 10px 28px rgba(0,0,0,0.55),
                    0 0 18px ${accent}22,
                    inset 0 0 0 1px rgba(255,255,255,0.04)
                `,
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: -24,
                    left: -10,
                    width: 90,
                    height: 90,
                    borderRadius: '50%',
                    background: accent,
                    opacity: 0.16,
                    filter: 'blur(28px)',
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    px: 1.25,
                    py: 1,
                    background: `linear-gradient(135deg, ${accent}22 0%, transparent 70%)`,
                    borderBottom: `1px solid ${accent}30`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: accent,
                        boxShadow: `0 0 10px ${accent}`,
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            color: '#fff',
                            letterSpacing: '0.02em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {point.playerLabel}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.58rem',
                            fontWeight: 700,
                            color: accent,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        {point.positionSource === 'killer' ? 'Killer position' : 'Victim position'}
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.85)',
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0,
                    }}
                >
                    {point.timeLabel}
                </Typography>
            </Box>

            <Box sx={{ px: 1.25, py: 1.1, position: 'relative', zIndex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.45, minWidth: 72 }}>
                        <AgentIcon
                            agent_name={point.killerAgent}
                            sx={{
                                width: 34,
                                height: 34,
                                opacity: point.killerAgent ? 1 : 0.35,
                            }}
                        />
                        {point.killerWeapon ? (
                            <WeaponIcon weapon={point.killerWeapon} width={36} height={16} />
                        ) : (
                            <Box sx={{ height: 16 }} />
                        )}
                        <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                            Killer
                        </Typography>
                    </Box>

                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '1rem', fontWeight: 700 }}>
                        →
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.45, minWidth: 72 }}>
                        <AgentIcon
                            agent_name={point.victimAgent}
                            sx={{ width: 34, height: 34 }}
                        />
                        {point.victimWeapon ? (
                            <WeaponIcon weapon={point.victimWeapon} width={36} height={16} />
                        ) : (
                            <Box sx={{ height: 16 }} />
                        )}
                        <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                            Victim
                        </Typography>
                    </Box>
                </Box>

                {(point.isHeadshot || point.isFirstBlood) && (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1 }}>
                        {point.isHeadshot && (
                            <Box
                                sx={{
                                    px: 0.75,
                                    py: 0.2,
                                    borderRadius: '999px',
                                    fontSize: '0.58rem',
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                    color: accent,
                                    background: `${accent}18`,
                                    border: `1px solid ${accent}40`,
                                }}
                            >
                                HS
                            </Box>
                        )}
                        {point.isFirstBlood && (
                            <Box
                                sx={{
                                    px: 0.75,
                                    py: 0.2,
                                    borderRadius: '999px',
                                    fontSize: '0.58rem',
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                    color: '#f1c40f',
                                    background: 'rgba(241,196,15,0.12)',
                                    border: '1px solid rgba(241,196,15,0.35)',
                                }}
                            >
                                FB
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function getEventPosition(kill: KillEventWithLocation, positionSource: KillPositionSource) {
    const location = positionSource === 'killer' ? kill.killer_location : kill.victim_location;
    const pos = location?.position;
    if (!pos || pos.x === null || pos.y === null) return null;
    return { x: pos.x, y: pos.y };
}

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

const TOOLTIP_WIDTH = 248;
const TOOLTIP_HEIGHT = 176;

function tooltipPosition(x: number, y: number, containerSize: number) {
    const left = clamp(x + 12, 8, Math.max(8, containerSize - TOOLTIP_WIDTH - 8));
    const preferBelow = y + 12 + TOOLTIP_HEIGHT <= containerSize - 8;
    const top = preferBelow
        ? y + 12
        : clamp(y - TOOLTIP_HEIGHT - 8, 8, Math.max(8, containerSize - TOOLTIP_HEIGHT - 8));
    return { left, top };
}

function clientToMapPoint(map: ReturnType<typeof useMap>, clientX: number, clientY: number) {
    const rect = map.getContainer().getBoundingClientRect();
    const size = map.getSize();
    return {
        x: clamp(clientX - rect.left, 0, size.x),
        y: clamp(clientY - rect.top, 0, size.y),
    };
}

function selectIndexesInBox(
    map: ReturnType<typeof useMap>,
    mapPoints: MapPoint[],
    start: { x: number; y: number },
    end: { x: number; y: number },
) {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    if (maxX - minX < 6 && maxY - minY < 6) return null;

    return mapPoints
        .filter((point) => {
            const containerPoint = map.latLngToContainerPoint([point.lat, point.lng]);
            return (
                containerPoint.x >= minX &&
                containerPoint.x <= maxX &&
                containerPoint.y >= minY &&
                containerPoint.y <= maxY
            );
        })
        .map((point) => point.selectionIndex);
}

/** Marquee for points mode: ignores presses on Circles so point clicks still work. */
function PointsMarqueeSelect(props: {
    mapPoints: MapPoint[];
    onMarqueeSelect: (selectedIndexes: number[]) => void;
    onEmptyClick: () => void;
}) {
    const map = useMap();
    const latestRef = useRef(props);
    latestRef.current = props;
    const draggingRef = useRef(false);
    const startRef = useRef<{ x: number; y: number } | null>(null);
    const [bounds, setBounds] = useState<LatLngBounds | null>(null);

    useEffect(() => {
        const onWindowMouseMove = (e: MouseEvent) => {
            if (!draggingRef.current || !startRef.current) return;
            const start = startRef.current;
            const end = clientToMapPoint(map, e.clientX, e.clientY);
            if (Math.abs(end.x - start.x) < 3 && Math.abs(end.y - start.y) < 3) return;

            const sw = map.containerPointToLatLng([
                Math.min(start.x, end.x),
                Math.max(start.y, end.y),
            ]);
            const ne = map.containerPointToLatLng([
                Math.max(start.x, end.x),
                Math.min(start.y, end.y),
            ]);
            setBounds(new LatLngBounds(sw, ne));
        };

        const onWindowMouseUp = (e: MouseEvent) => {
            if (!draggingRef.current || !startRef.current) return;
            draggingRef.current = false;
            const start = startRef.current;
            startRef.current = null;
            setBounds(null);

            const end = clientToMapPoint(map, e.clientX, e.clientY);
            const selectedIndexes = selectIndexesInBox(map, latestRef.current.mapPoints, start, end);
            if (selectedIndexes) {
                latestRef.current.onEmptyClick();
                latestRef.current.onMarqueeSelect(selectedIndexes);
            } else {
                latestRef.current.onEmptyClick();
            }

            window.removeEventListener('mousemove', onWindowMouseMove);
            window.removeEventListener('mouseup', onWindowMouseUp);
        };

        const onMapMouseDown = (e: any) => {
            if (e.originalEvent.button !== 0) return;
            // Ignore presses that start on a vector point so point tooltips still work
            const target = e.originalEvent.target as HTMLElement | null;
            if (target?.closest?.('.leaflet-interactive')) return;

            draggingRef.current = true;
            startRef.current = { x: e.containerPoint.x, y: e.containerPoint.y };
            setBounds(null);

            window.addEventListener('mousemove', onWindowMouseMove);
            window.addEventListener('mouseup', onWindowMouseUp);
        };

        map.on('mousedown', onMapMouseDown);
        return () => {
            map.off('mousedown', onMapMouseDown);
            window.removeEventListener('mousemove', onWindowMouseMove);
            window.removeEventListener('mouseup', onWindowMouseUp);
        };
    }, [map]);

    if (!bounds) return null;
    return (
        <Rectangle
            bounds={bounds}
            pathOptions={{
                color: 'rgba(80, 180, 255, 0.95)',
                weight: 1,
                fillColor: 'rgba(80, 180, 255, 0.18)',
                fillOpacity: 1,
                interactive: false,
            }}
        />
    );
}

/** Marquee for heatmap mode: full overlay; hover nearest kill for tooltip. */
function HeatmapMarqueeSelect(props: {
    mapPoints: MapPoint[];
    onMarqueeSelect: (selectedIndexes: number[]) => void;
    onPointHover: (point: MapPoint, containerX: number, containerY: number) => void;
    onEmptyHover: () => void;
}) {
    const map = useMap();
    const latestRef = useRef(props);
    latestRef.current = props;

    useEffect(() => {
        const container = map.getContainer();
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.inset = '0';
        overlay.style.zIndex = '1000';
        overlay.style.cursor = 'crosshair';
        overlay.style.userSelect = 'none';

        const box = document.createElement('div');
        box.style.position = 'absolute';
        box.style.border = '1px solid rgba(80, 180, 255, 0.95)';
        box.style.background = 'rgba(80, 180, 255, 0.18)';
        box.style.boxShadow = 'inset 0 0 0 1px rgba(255,255,255,0.15)';
        box.style.pointerEvents = 'none';
        box.style.display = 'none';
        overlay.appendChild(box);
        container.appendChild(overlay);

        let dragging = false;
        let start: { x: number; y: number } | null = null;

        const updateBox = (end: { x: number; y: number }) => {
            if (!start) {
                box.style.display = 'none';
                return;
            }
            box.style.display = 'block';
            box.style.left = `${Math.min(start.x, end.x)}px`;
            box.style.top = `${Math.min(start.y, end.y)}px`;
            box.style.width = `${Math.abs(end.x - start.x)}px`;
            box.style.height = `${Math.abs(end.y - start.y)}px`;
        };

        const nearestPoint = (x: number, y: number, maxDist = 14) => {
            let best: MapPoint | null = null;
            let bestDist = maxDist;
            latestRef.current.mapPoints.forEach((point) => {
                if (!point.enabled) return;
                const containerPoint = map.latLngToContainerPoint([point.lat, point.lng]);
                const dist = Math.hypot(containerPoint.x - x, containerPoint.y - y);
                if (dist <= bestDist) {
                    best = point;
                    bestDist = dist;
                }
            });
            return best;
        };

        const detachWindowListeners = () => {
            window.removeEventListener('mousemove', onWindowMouseMove);
            window.removeEventListener('mouseup', onWindowMouseUp);
        };

        const finish = (end: { x: number; y: number }) => {
            if (!dragging || !start) return;
            dragging = false;
            const origin = start;
            start = null;
            box.style.display = 'none';
            detachWindowListeners();

            const selectedIndexes = selectIndexesInBox(map, latestRef.current.mapPoints, origin, end);
            if (selectedIndexes === null) return;

            latestRef.current.onEmptyHover();
            latestRef.current.onMarqueeSelect(selectedIndexes);
        };

        const onWindowMouseMove = (e: MouseEvent) => {
            if (!dragging || !start) return;
            updateBox(clientToMapPoint(map, e.clientX, e.clientY));
        };

        const onWindowMouseUp = (e: MouseEvent) => {
            if (!dragging || !start) return;
            finish(clientToMapPoint(map, e.clientX, e.clientY));
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            e.preventDefault();
            dragging = true;
            start = clientToMapPoint(map, e.clientX, e.clientY);
            latestRef.current.onEmptyHover();
            updateBox(start);
            window.addEventListener('mousemove', onWindowMouseMove);
            window.addEventListener('mouseup', onWindowMouseUp);
        };

        const onMouseMove = (e: MouseEvent) => {
            if (dragging) return;
            const pos = clientToMapPoint(map, e.clientX, e.clientY);
            const hit = nearestPoint(pos.x, pos.y);
            if (hit) latestRef.current.onPointHover(hit, pos.x, pos.y);
            else latestRef.current.onEmptyHover();
        };

        const onMouseLeave = () => {
            if (!dragging) latestRef.current.onEmptyHover();
        };

        overlay.addEventListener('mousedown', onMouseDown);
        overlay.addEventListener('mousemove', onMouseMove);
        overlay.addEventListener('mouseleave', onMouseLeave);

        return () => {
            overlay.removeEventListener('mousedown', onMouseDown);
            overlay.removeEventListener('mousemove', onMouseMove);
            overlay.removeEventListener('mouseleave', onMouseLeave);
            detachWindowListeners();
            if (container.contains(overlay)) {
                container.removeChild(overlay);
            }
        };
    }, [map]);

    return null;
}

function HighlightedPointSync(props: {
    point: MapPoint | null;
    onSync: (tooltip: { point: MapPoint; x: number; y: number } | null) => void;
}) {
    const map = useMap();

    useEffect(() => {
        if (!props.point) {
            props.onSync(null);
            return;
        }
        const containerPoint = map.latLngToContainerPoint([props.point.lat, props.point.lng]);
        props.onSync({
            point: props.point,
            x: containerPoint.x,
            y: containerPoint.y,
        });
    }, [map, props.point, props.onSync]);

    return null;
}

export function HeatmapKillsRender(props: {
    map: Maps;
    id: string;
    children?: React.ReactNode;
    kills: SelectableKillEvent[];
    mode?: KillMapRenderMode;
    positionSource?: KillPositionSource;
    onMarqueeSelect?: (selectedIndexes: number[]) => void;
    highlightedIndex?: number | null;
}) {
    const mode = props.mode ?? 'heatmap';
    const positionSource = props.positionSource ?? 'victim';
    const highlightedIndex = props.highlightedIndex ?? null;
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState(null as number | null);
    const [pointTooltip, setPointTooltip] = useState<null | { point: MapPoint; x: number; y: number }>(null);
    const [hoverTooltip, setHoverTooltip] = useState<null | { point: MapPoint; x: number; y: number }>(null);

    useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    if (entry.contentRect) {
                        const size = Math.floor(Math.min(entry.contentRect.width, entry.contentRect.height));
                        setContainerSize(size+1);
                    }
                }
            });

            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    useEffect(() => {
        setPointTooltip(null);
        setHoverTooltip(null);
    }, [mode, positionSource, props.kills]);

    const mapPoints = useMemo(() => {
        if (!containerSize) return [] as MapPoint[];
        return props.kills.reduce((acc, kill, index) => {
            const eventPos = getEventPosition(kill, positionSource);
            if (!eventPos) return acc;
            const position_normalized = positionToMapBounds(eventPos, props.map);
            const killerPos = getEventPosition(kill, 'killer');
            const victimPos = getEventPosition(kill, 'victim');
            const killerNormalized = killerPos ? positionToMapBounds(killerPos, props.map) : null;
            const victimNormalized = victimPos ? positionToMapBounds(victimPos, props.map) : null;
            const playerLabel = (kill.player || '').trim() || 'Unknown';
            acc.push({
                selectionIndex: kill.selectionIndex ?? index,
                enabled: kill.enabled ?? true,
                lat: position_normalized.x * containerSize,
                lng: position_normalized.y * containerSize,
                label: positionSource === 'killer' ? (kill.killer_agent || 'Killer') : kill.victim_agent,
                playerLabel,
                timeLabel: formatDuration(kill.time_in_round),
                color: getPlayerPointColor(playerLabel),
                killerAgent: kill.killer_agent || '',
                victimAgent: kill.victim_agent || '',
                killerWeapon: kill.killer_weapon || '',
                victimWeapon: kill.victim_weapon || '',
                isHeadshot: !!kill.is_headshot,
                isFirstBlood: !!kill.is_first_blood,
                positionSource,
                killerLat: killerNormalized ? killerNormalized.x * containerSize : null,
                killerLng: killerNormalized ? killerNormalized.y * containerSize : null,
                victimLat: victimNormalized ? victimNormalized.x * containerSize : null,
                victimLng: victimNormalized ? victimNormalized.y * containerSize : null,
            });
            return acc;
        }, [] as MapPoint[]);
    }, [props.kills, props.map, containerSize, positionSource]);

    const enabledMapPoints = useMemo(
        () => mapPoints.filter((point) => point.enabled),
        [mapPoints],
    );

    const focusIndex = pointTooltip?.point.selectionIndex ?? highlightedIndex ?? null;

    const highlightedPoint = useMemo(() => {
        if (highlightedIndex == null) return null;
        return mapPoints.find((point) => point.selectionIndex === highlightedIndex) || null;
    }, [highlightedIndex, mapPoints]);

    const focusedPoint = useMemo(() => {
        if (focusIndex == null) return null;
        return mapPoints.find((point) => point.selectionIndex === focusIndex) || null;
    }, [focusIndex, mapPoints]);

    const handleHoverSync = React.useCallback((tooltip: { point: MapPoint; x: number; y: number } | null) => {
        setHoverTooltip(tooltip);
    }, []);

    const heatPoints = useMemo(() => {
        const source = focusIndex == null
            ? enabledMapPoints
            : enabledMapPoints.filter((point) => point.selectionIndex === focusIndex);
        return source.map((point) => ({
            lat: point.lat,
            lng: point.lng,
            intensity: 1,
        } as SimpleHeatPoint));
    }, [enabledMapPoints, focusIndex]);

    const activeTooltip = hoverTooltip || pointTooltip;
    const map_url = `https://res.cloudinary.com/xenesis/image/upload/c_scale,h_${containerSize},w_${containerSize}/valAssets/${props.map ? props.map.id : props.id}.png`;

    const focusedHasBothPositions = !!(
        focusedPoint
        && focusedPoint.killerLat != null
        && focusedPoint.killerLng != null
        && focusedPoint.victimLat != null
        && focusedPoint.victimLng != null
    );

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                userSelect: 'none',
                position: 'relative',
            }}
        >
            {containerSize ? (
                <Box sx={{ position: 'relative', width: containerSize, height: containerSize }}>
                    <MapContainer
                        center={[containerSize / 2, containerSize / 2]}
                        scrollWheelZoom={false}
                        zoom={0}
                        dragging={false}
                        minZoom={0}
                        maxZoom={0}
                        attributionControl={false}
                        zoomControl={false}
                        doubleClickZoom={false}
                        crs={CRS.Simple}
                        style={{ width: containerSize, height: containerSize, cursor: props.onMarqueeSelect ? 'crosshair' : 'default' }}
                        bounds={[
                            [0, 0],
                            [containerSize, containerSize],
                        ]}
                    >
                        <ImageOverlay
                            url={map_url}
                            bounds={[
                                [0, 0],
                                [containerSize, containerSize],
                            ]}
                        />
                        {mode === 'heatmap' ? (
                            <SimpleHeatLayer
                                points={heatPoints}
                                radius={Math.max(18, Math.round(containerSize * 0.035))}
                                blur={Math.max(12, Math.round(containerSize * 0.025))}
                                max={Math.max(3, Math.ceil(Math.max(heatPoints.length, 1) / 40))}
                                minOpacity={0.15}
                                opacity={focusIndex == null ? 0.9 : 0.75}
                            />
                        ) : (
                            enabledMapPoints.map((point) => {
                                const isFocused = focusIndex === point.selectionIndex;
                                const isDimmed = focusIndex != null && !isFocused;
                                return (
                                    <Circle
                                        radius={isFocused ? 9 : 6}
                                        key={point.selectionIndex}
                                        center={[point.lat, point.lng]}
                                        interactive={true}
                                        pathOptions={{
                                            color: isDimmed ? DIMMED_POINT_COLOR : point.color,
                                            fillColor: isDimmed ? DIMMED_POINT_COLOR : point.color,
                                            fillOpacity: isDimmed ? 0.2 : isFocused ? 0.95 : 0.7,
                                            opacity: isDimmed ? 0.35 : 0.95,
                                            weight: isFocused ? 3 : 2,
                                        }}
                                        eventHandlers={{
                                            mouseover: (e) => {
                                                setPointTooltip({
                                                    point,
                                                    x: e.containerPoint.x,
                                                    y: e.containerPoint.y,
                                                });
                                            },
                                            mouseout: () => {
                                                setPointTooltip((prev) => (
                                                    prev?.point.selectionIndex === point.selectionIndex ? null : prev
                                                ));
                                            },
                                        }}
                                    />
                                );
                            })
                        )}

                        {focusedPoint && focusedHasBothPositions && (
                            <>
                                <Polyline
                                    positions={[
                                        [focusedPoint.killerLat as number, focusedPoint.killerLng as number],
                                        [focusedPoint.victimLat as number, focusedPoint.victimLng as number],
                                    ]}
                                    pathOptions={{
                                        color: focusedPoint.color,
                                        weight: 2,
                                        opacity: 0.85,
                                        dashArray: '6 4',
                                    }}
                                />
                                <Circle
                                    radius={8}
                                    center={[focusedPoint.killerLat as number, focusedPoint.killerLng as number]}
                                    interactive={false}
                                    pathOptions={{
                                        color: focusedPoint.color,
                                        fillColor: focusedPoint.color,
                                        fillOpacity: positionSource === 'killer' ? 0.9 : 0.35,
                                        opacity: 1,
                                        weight: 3,
                                    }}
                                />
                                <Circle
                                    radius={8}
                                    center={[focusedPoint.victimLat as number, focusedPoint.victimLng as number]}
                                    interactive={false}
                                    pathOptions={{
                                        color: '#ffffff',
                                        fillColor: focusedPoint.color,
                                        fillOpacity: positionSource === 'victim' ? 0.9 : 0.35,
                                        opacity: 1,
                                        weight: 3,
                                    }}
                                />
                                {/* Soft rings to label roles */}
                                <Circle
                                    radius={13}
                                    center={[focusedPoint.killerLat as number, focusedPoint.killerLng as number]}
                                    interactive={false}
                                    pathOptions={{
                                        color: focusedPoint.color,
                                        fillOpacity: 0,
                                        opacity: 0.7,
                                        weight: 1.5,
                                        dashArray: positionSource === 'killer' ? undefined : '3 3',
                                    }}
                                />
                                <Circle
                                    radius={13}
                                    center={[focusedPoint.victimLat as number, focusedPoint.victimLng as number]}
                                    interactive={false}
                                    pathOptions={{
                                        color: '#ffffff',
                                        fillOpacity: 0,
                                        opacity: 0.7,
                                        weight: 1.5,
                                        dashArray: positionSource === 'victim' ? undefined : '3 3',
                                    }}
                                />
                            </>
                        )}

                        {focusedPoint && !focusedHasBothPositions && (
                            <Circle
                                radius={mode === 'points' ? 14 : 10}
                                center={[focusedPoint.lat, focusedPoint.lng]}
                                interactive={false}
                                pathOptions={{
                                    color: focusedPoint.color,
                                    fillColor: focusedPoint.color,
                                    fillOpacity: 0.15,
                                    opacity: 0.9,
                                    weight: 2,
                                    dashArray: '4 3',
                                }}
                            />
                        )}

                        <HighlightedPointSync
                            point={highlightedPoint}
                            onSync={handleHoverSync}
                        />
                        {props.onMarqueeSelect && mode === 'points' && (
                            <PointsMarqueeSelect
                                mapPoints={mapPoints}
                                onMarqueeSelect={props.onMarqueeSelect}
                                onEmptyClick={() => setPointTooltip(null)}
                            />
                        )}
                        {props.onMarqueeSelect && mode === 'heatmap' && (
                            <HeatmapMarqueeSelect
                                mapPoints={mapPoints}
                                onMarqueeSelect={props.onMarqueeSelect}
                                onPointHover={(point, x, y) => setPointTooltip({ point, x, y })}
                                onEmptyHover={() => setPointTooltip(null)}
                            />
                        )}
                    </MapContainer>

                    {activeTooltip && (() => {
                        const pos = tooltipPosition(activeTooltip.x, activeTooltip.y, containerSize);
                        return (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: pos.left,
                                    top: pos.top,
                                    zIndex: 1100,
                                    pointerEvents: 'none',
                                    width: TOOLTIP_WIDTH,
                                    transform: 'scale(1.02)',
                                    filter: `drop-shadow(0 0 10px ${activeTooltip.point.color}66)`,
                                    transition: 'transform 0.12s ease, filter 0.12s ease',
                                }}
                            >
                                <KillPointTooltip point={activeTooltip.point} />
                            </Box>
                        );
                    })()}
                </Box>
            ) : <CircularProgress/>}
        </Box>
    );
}
